import { useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ErrorBoundary } from '@/components/common/error-boundary'
import { usePanelSelectionStore } from '@/stores/panel-selection-store'
import { usePanelActivityStore } from '@/stores/panel-activity-store'

interface PanelWrapperProps {
  panelId: string
  panelType?: string
  isActive: boolean
  onActivate: () => void
  children: ReactNode
}

/**
 * Generic panel wrapper — wraps any panel component within a mosaic tile.
 * Handles active-panel highlighting, click-to-activate, AI panel selection,
 * and AI activity indicator (shimmer + message).
 */
export function PanelWrapper({
  panelId,
  panelType,
  isActive,
  onActivate,
  children,
}: PanelWrapperProps) {
  const wrapperRef = useRef<HTMLDivElement>(null)

  const selectionActive = usePanelSelectionStore((s) => s.active)
  const selectionType = usePanelSelectionStore((s) => s.panelType)
  const selectPanel = usePanelSelectionStore((s) => s.selectPanel)

  const activityPanelId = usePanelActivityStore((s) => s.activePanelId)
  const activityMessage = usePanelActivityStore((s) => s.message)
  const isBeingModified = activityPanelId === panelId

  const isSelectable = selectionActive && (!selectionType || selectionType === panelType)
  const isDimmed = selectionActive && !isSelectable

  // Find the parent .mosaic-window to portal overlays to it (covers toolbar + body)
  const getMosaicWindow = () => {
    return wrapperRef.current?.closest('.mosaic-window') as HTMLElement | null
  }

  return (
    <div
      ref={wrapperRef}
      data-panel-id={panelId}
      data-panel-type={panelType}
      className={cn(
        'relative h-full w-full overflow-hidden',
        isActive && !selectionActive && !isBeingModified && 'ring-1 ring-primary',
        isDimmed && 'opacity-30 pointer-events-none',
      )}
      onMouseDown={selectionActive ? undefined : onActivate}
    >
      <ErrorBoundary inline>{children}</ErrorBoundary>

      {/* AI activity overlay — portaled to mosaic-window to cover toolbar + body */}
      {isBeingModified &&
        wrapperRef.current &&
        getMosaicWindow() &&
        createPortal(
          <div className="absolute inset-0 z-40 pointer-events-none rounded-md overflow-hidden">
            {/* Shimmer gradient */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/15 to-transparent animate-[shimmer_2s_ease-in-out_infinite]" />

            {/* Border glow */}
            <div className="absolute inset-0 ring-2 ring-primary/40 ring-inset rounded-md" />

            {/* Message badge */}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 rounded-full bg-background/90 border border-primary/30 px-3 py-1.5 shadow-lg backdrop-blur-sm">
              <Sparkles size={12} className="text-primary animate-pulse" />
              <span className="text-[11px] font-medium text-primary">{activityMessage}</span>
            </div>
          </div>,
          getMosaicWindow()!,
        )}

      {/* Panel selection overlay — portaled to mosaic-window */}
      {isSelectable &&
        wrapperRef.current &&
        getMosaicWindow() &&
        createPortal(
          <div
            className="absolute inset-0 z-50 flex cursor-pointer items-center justify-center rounded-md overflow-hidden bg-primary/10 ring-2 ring-primary ring-inset animate-pulse"
            onClick={(e) => {
              e.stopPropagation()
              selectPanel(panelId)
            }}
          >
            <span className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-lg">
              Select this panel
            </span>
          </div>,
          getMosaicWindow()!,
        )}
    </div>
  )
}
